import { useMemo, useEffect } from 'react';
import { format } from 'date-fns';

import { CalendarDropdownProps } from './type';
import Icon from '../../Icon';
import Typography from '../../Typography';
import { TucarTheme } from '../../theme';
import { getMonthName, isValidDate } from '../utils';

import {
  DropDownGoBackButtonWrapper,
  DropdownItemStyled,
  DropdownItemWrapperStyled,
  DropdownItemContainerStyled,
} from './styled';

const itemHeight = 35;
const itemMargin = 11.5;

const CalendarDropdown = ({ ...props }: CalendarDropdownProps) => {
  const {
    selectedView,
    filteredViews,
    selectedYear,
    selectedDate,
    selectedMonth,
    inputFormat,
    setters,
    isOpen,
    handleDisableMonth,
    handleDisableYear,
    maxYear,
    minYear,
  } = props;

  const { setIsOpen, setSelectedView, handleSelectedValuesChange, setDebouncedValue } = setters;

  const availableYears: number[] = useMemo(
    () => [...Array(maxYear + 1 - minYear).keys()].map((year) => year + minYear),
    [maxYear, minYear],
  );

  const scrollToSelected = (dropdown: { scrollTop: number }, selectedIndex: number | null) => {
    if (dropdown && selectedIndex !== null) {
      // eslint-disable-next-line no-param-reassign
      dropdown.scrollTop = selectedIndex * itemHeight + itemMargin;
    }
  };

  const handleDateChange = (
    dateValue: Date,
    itemValue: number,
    changeItemType: 'year' | 'month',
  ) => {
    handleSelectedValuesChange({ date: dateValue, [changeItemType]: itemValue });
    setDebouncedValue(format(dateValue, inputFormat));
  };

  const handleDropdownItemClick = (value: number, type: 'year' | 'month') => {
    if (type === 'year' && handleDisableYear(value)) return;
    if (type === 'month' && handleDisableMonth(value, selectedYear)) return;
    const baseDate = isValidDate(selectedDate) ? selectedDate : new Date();
    baseDate.setMonth(type === 'year' ? selectedMonth : value);
    baseDate.setFullYear(type === 'year' ? value : selectedYear);
    if (type === 'year') {
      handleDateChange(baseDate, value, type);
      if (filteredViews.includes('month')) setSelectedView('month');
      else setIsOpen(false);
    } else {
      handleDateChange(baseDate, value, type);
      if (filteredViews.includes('year')) setSelectedView('year');
      setIsOpen(false);
    }
  };

  useEffect(() => {
    if (availableYears.length <= 1 && selectedView === 'year') setSelectedView('month');
  }, [availableYears, selectedView, isOpen]);

  const renderYears = () => (
    <DropdownItemContainerStyled
      key="year-dropdown"
      ref={(dropdown) => scrollToSelected(dropdown, selectedYear ? selectedYear - minYear : null)}
    >
      {availableYears.map((currentYear: number) => {
        const yearLabel = currentYear.toString();
        const isYearDisabled = handleDisableYear(currentYear);
        return (
          <DropdownItemStyled
            disabled={isYearDisabled}
            key={currentYear}
            name={yearLabel}
            onClick={() => handleDropdownItemClick(currentYear, 'year')}
            selected={selectedYear === currentYear}
            value={yearLabel}
          >
            <Typography
              data-disabled={isYearDisabled}
              style={{
                lineHeight: '17px',
                ...(isYearDisabled
                  ? { color: TucarTheme.colors.gray600, cursor: 'not-allowed' }
                  : {}),
              }}
            >
              {currentYear}
            </Typography>
          </DropdownItemStyled>
        );
      })}
    </DropdownItemContainerStyled>
  );

  const renderMonths = () => (
    <DropdownItemContainerStyled
      key="month-dropdown"
      ref={(dropdown) => scrollToSelected(dropdown, selectedMonth)}
    >
      {filteredViews.includes('year') && availableYears.length > 1 && (
        <DropDownGoBackButtonWrapper onClick={() => setSelectedView('year')}>
          <Icon
            color="conic-gradient(from 51deg, #FFF, #0057B8)"
            name="arrowBackIosFilled"
            size="16px"
          />
          <Typography
            style={{
              color: TucarTheme.colors.blue500,
              lineHeight: '17px',
            }}
          >
            Atrás
          </Typography>
        </DropDownGoBackButtonWrapper>
      )}
      {[...Array(12).keys()].map((currentMonth) => {
        const monthLabel = currentMonth.toString();
        const isMonthDisabled = handleDisableMonth(currentMonth, selectedYear);
        return (
          <DropdownItemStyled
            disabled={isMonthDisabled}
            key={currentMonth}
            name={monthLabel}
            onClick={() => handleDropdownItemClick(currentMonth, 'month')}
            selected={selectedMonth === currentMonth}
            value={monthLabel}
          >
            <Typography
              data-disabled={isMonthDisabled}
              style={{
                marginLeft: '25px',
                lineHeight: '17px',
                ...(isMonthDisabled
                  ? { color: TucarTheme.colors.gray600, cursor: 'not-allowed' }
                  : {}),
              }}
            >
              {getMonthName(currentMonth)}
            </Typography>
          </DropdownItemStyled>
        );
      })}
    </DropdownItemContainerStyled>
  );

  const renderDropDownContent = () => {
    if (selectedView === 'year' && filteredViews.includes('year')) {
      return renderYears();
    }
    if (selectedView === 'month' && filteredViews.includes('month')) {
      return renderMonths();
    }
    return <div />;
  };

  return (
    <DropdownItemWrapperStyled open={isOpen}>
      {isOpen ? renderDropDownContent() : <div />}
    </DropdownItemWrapperStyled>
  );
};

export default CalendarDropdown;
